import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { User as AuthUser } from 'firebase/auth';
import { User } from '@synergetx/puppet-interview-types';

import { auth } from '../modules/firebase/auth';
import { initializeUser } from '@modules/api';
import invariant from 'tiny-invariant';
import { logError, setSentryUser } from '@modules/logging';
import { onUserUpdate } from '@modules/firebase/user';
import { APP_ENV } from '@modules/consts';

interface Props {
    children: ReactNode;
}

export interface AuthContextValues {
    authUser: AuthUser | null;
    databaseUser: User | null;
}

export const AuthProviderContext = createContext<AuthContextValues | null>(null);

export default function FirebaseAuthentication({ children }: Props) {
    // Lazy initializer: without the arrow, `auth()` re-runs every render for nothing.
    const [authUser, setAuthUser] = useState<AuthUser | null>(() => auth().currentUser);
    const [databaseUser, setDatabaseUser] = useState<User | null>(null);

    useEffect(() => {
        // Make sure we un-register Firebase observers when the component unmounts.
        return auth().onAuthStateChanged((user) => {
            if (user) {
                // Saves first-time users. Fire-and-forget: sign-in already succeeded.
                initializeUser(user.uid).catch(logError);
                setAuthUser(user);
                setSentryUser(user);
            } else {
                setSentryUser(null);
                setAuthUser(null);
            }
        });
    }, []);

    useEffect(() => {
        if (authUser?.uid) {
            return onUserUpdate(authUser.uid, (user) => {
                if (user) {
                    setDatabaseUser(user);
                }
            });
        }
    }, [authUser?.uid]);

    const contextValue = useMemo((): AuthContextValues => {
        return { authUser, databaseUser };
    }, [authUser, databaseUser]);

    // Always provide the context - let routing handle auth requirements
    return <AuthProviderContext value={contextValue}>{children}</AuthProviderContext>;
}

export const useAuthUser = () => {
    const ctx = useContext(AuthProviderContext);

    invariant(ctx, 'useAuthUser called outside of AuthProviderContext');

    return ctx.authUser;
};

export const useDatabaseUser = () => {
    const ctx = useContext(AuthProviderContext);

    invariant(ctx, 'useDatabaseUser called outside of AuthProviderContext');

    return ctx.databaseUser;
};

// Only allow certain users to approve access requests
const ACCESS_ADMINS = [
    'aimlyons@gmail.com', // Aimee Lyons
    'alyons6@nd.edu', // Aimee Lyons
    'ndsa@nd.edu', // Nikhit
    'team@synergetx.io', // Synergetx Team
    'team@puppetinterview.com', // Puppet Interview Team
];

// Only allow certain users to approve access requests
export const useIsAccessAdmin = () => {
    const authUser = useAuthUser();
    const email = authUser?.email || '';

    return useMemo(() => {
        if (['crest', 'puppet-interview'].includes(APP_ENV) && email) {
            return ACCESS_ADMINS.includes(email);
        } else {
            return false;
        }
    }, [email]);
};
